import { useEffect, useState, useRef } from 'react';
import { hysteriFetch, wrappers } from 'hysterics';
import { sanitizeListingConfigId } from '../utils/submitSearchUtils';
import { validateSearchResults } from '../utils/validateSearchResults';

const nlsFetchWrapper = hysteriFetch(
	'ws-inv-text-search-nls-suggestions-fetch'
);

/**
 * Custom hook to handle NLS fallback when FreeFormSearchService returns no facetInfo
 *
 * This hook will:
 * 1. Check if input has at least 2 words
 * 2. Check if there is no facetInfo from the primary API
 * 3. Wait for the configured timeout duration
 * 4. Make an NLS request to get fallback suggestions
 *
 * @param {string} input - The search input string
 * @param {Object} primaryResponse - The response from the primary search API
 * @param {Object} options - Configuration options for the NLS request
 * @param {string} options.siteId - The site identifier
 * @param {string} options.pageAlias - The page alias identifier
 * @param {string} options.widgetName - The name of the widget making the request
 * @param {string} options.windowId - The window identifier
 * @param {string} options.deviceType - The device type (e.g., 'DESKTOP', 'MOBILE')
 * @param {string} options.locale - The locale string (e.g., 'en_US')
 * @param {number} options.timeout - The timeout in milliseconds before making NLS request
 * @returns {Object} Object containing response and isComplete
 * @returns {Object|null} return.response - The NLS suggestions response or null if no NLS results
 * @returns {boolean} return.isComplete - Whether the NLS fallback process has completed
 */
const useNLSFallback = (
	input,
	primaryResponse,
	{ siteId, pageAlias, widgetName, windowId, deviceType, locale, timeout }
) => {
	const [nlsResponse, setNlsResponse] = useState(null);
	const [isComplete, setIsComplete] = useState(false);
	const timeoutRef = useRef(null);
	const requestCounterRef = useRef(0);

	useEffect(() => {
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		// Reset NLS response and completion status when input changes
		setNlsResponse(null);
		setIsComplete(false);

		// Check if primary response exists
		const hasPrimaryResponse =
			primaryResponse !== null && primaryResponse !== undefined;

		// Validate input - is the input suitable for NLS search?
		const hasMultipleWords = input
			? /\b\w+\b.*\b\w+\b/.test(input.trim())
			: false;

		const { hasSuggestions, hasVehicleMatch } =
			validateSearchResults(primaryResponse);
		const primaryHasResults = hasSuggestions || hasVehicleMatch;

		// Determine if we should fetch from NLS
		const shouldFetchNLS =
			hasMultipleWords && hasPrimaryResponse && !primaryHasResults;

		if (shouldFetchNLS) {
			// Increment request counter and capture it for this request
			requestCounterRef.current += 1;
			const currentRequestId = requestCounterRef.current;

			// Set timeout to make NLS request.
			// Reason: If user is typing slowly, we don't want to send partial searches to the AI system.
			timeoutRef.current = setTimeout(async () => {
				try {
					// Get listing config from DDC.InvData prefs
					const listingConfigIdPref =
						window.DDC?.InvData?.prefs?.['listing.config.id'] ||
						'auto-new,auto-used';

					const listingConfigId =
						sanitizeListingConfigId(listingConfigIdPref);

					const facetInstanceId =
						window.DDC?.InvData?.prefs?.facetInstanceId ||
						'listing';

					// Build URL for existing nlInventorySearch endpoint
					const queryParams = new URLSearchParams({
						q: input
					});

					const nlsUrl = `/api/widget/ws-inv-text-search/nlInventorySearch/${siteId}/${locale}?${queryParams.toString()}`;

					const nlsResult = await nlsFetchWrapper(
						nlsUrl,
						{
							method: 'POST',
							headers: {
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({
								device: deviceType,
								pageAlias,
								windowId,
								widgetName,
								listingConfigId,
								facetInstanceId
							})
						},
						{
							[wrappers.timeout.contextKey]: {
								timeout: 15000
							},
							[wrappers.retry.contextKey]: {
								retries: 0
							}
						}
					);

					// Only process if this is still the latest request
					if (currentRequestId === requestCounterRef.current) {
						// Only set the NLS response if we got valid suggestions
						if (validateSearchResults(nlsResult).hasSuggestions) {
							setNlsResponse(nlsResult);
						}
						// Always mark complete when fetch finishes (with or without results)
						setIsComplete(true);
					}
				} catch (error) {
					// Error fetching NLS fallback suggestions
					// Only clear NLS response if this is still the latest request
					if (currentRequestId === requestCounterRef.current) {
						setNlsResponse(null);
						setIsComplete(true);
					}
				}
			}, timeout);
		} else if (hasPrimaryResponse) {
			// Not fetching from NLS because:
			// - Primary response has suggestions or vehicles, OR
			// - Input is invalid (empty or single word)
			// Mark complete since we've decided not to fetch
			setIsComplete(true);
		} else {
			// else: waiting for primary response, don't mark complete yet
		}

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};
	}, [
		input,
		primaryResponse,
		siteId,
		pageAlias,
		widgetName,
		windowId,
		deviceType,
		locale,
		timeout
	]);

	return { response: nlsResponse, isComplete };
};

export default useNLSFallback;
